import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { apiRequest } from '../api/client.js';
import { LanguageFlags } from '../components/LanguageFlags.js';
import { Logo } from '../components/Logo.js';
import styles from './ConnectPage.module.css';

/**
 * Главная страница и она же экран входа: рассказ о продукте и единственное действие —
 * подключение сайта Jira. Разделять их незачем, пока приложение умеет ровно одно.
 *
 * Ссылка на согласие не формируется на клиенте: client_id и области доступа остаются
 * на сервере, браузер только переходит по выданному адресу.
 *
 * Вёрстка — по макету «Jisy Connect» (jisy-premium-landing-page).
 */
export function ConnectPage(): React.ReactElement {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);

  // Пользователь мог отказать в доступе на экране Atlassian — это не сбой приложения.
  const declined = params.get('error') !== null;

  async function connect(): Promise<void> {
    setPending(true);
    setFailed(false);
    try {
      const { authUrl } = await apiRequest<{ authUrl: string }>('/api/auth/jira/start', {
        method: 'POST',
        body: {},
      });
      window.location.assign(authUrl);
    } catch {
      setPending(false);
      setFailed(true);
    }
  }

  return (
    <div className={styles.page}>
      <header className={styles.top}>
        <div className={`${styles.row} ${styles.topRow}`}>
          <div className={styles.brand}>
            <Logo height={22} tone="onColor" />
            <span className={styles.badge}>Jira Cloud</span>
          </div>
          <LanguageFlags />
        </div>
      </header>

      <main className={`${styles.row} ${styles.main}`}>
        <h1 className={styles.title}>{t('connect.title')}</h1>
        <p className={styles.claim}>{t('connect.claim')}</p>
        <p className={styles.lead}>{t('connect.description')}</p>

        <div className={styles.card}>
          <div className={styles.cardHead}>
            <span className={styles.tiles} aria-hidden="true">
              <span className={styles.tilesGrid}>
                <span />
                <span />
                <span />
                <span />
              </span>
            </span>
            <span className={styles.step}>
              <span className={styles.pulse} aria-hidden="true" />
              {t('connect.step')}
            </span>
          </div>

          <h2 className={styles.cardTitle}>{t('connect.cardTitle')}</h2>
          <p className={styles.cardText}>{t('connect.cardText')}</p>

          {(declined || failed) && (
            <p className={styles.notice} role="status">
              {declined ? t('connect.declined') : t('connect.failed')}
            </p>
          )}

          <button type="button" className={styles.action} onClick={() => void connect()} disabled={pending}>
            <span className={styles.actionMark} aria-hidden="true">
              <span />
              <span />
              <span />
              <span />
            </span>
            {pending ? t('connect.connecting') : t('connect.action')}
          </button>

          <p className={styles.note}>{t('connect.note')}</p>

          <div className={styles.footnote}>
            <span>{t('connect.again')}</span>
            <button type="button" className={styles.link} onClick={() => void connect()}>
              {t('connect.chooseAnother')}
            </button>
          </div>
        </div>
      </main>

      <footer className={styles.foot}>
        <div className={`${styles.row} ${styles.footInner}`}>
          <h2 className={styles.footTitle}>{t('connect.footTitle')}</h2>
          <p className={styles.footText}>{t('connect.footText')}</p>
          <div className={styles.footLine}>
            <span>© {new Date().getFullYear()} Jisy</span>
            <span>{t('connect.footMade')}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
